import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { NavLink, useSearchParams } from "react-router-dom";
import {
    MobilizationColumns,
    MobilizationFilterColumn,
} from "../../components/data-table/columns/Mobilization";
import MobilizationTableRow from "../../components/data-table/row/MobilizationTableRow";
import PageHeading from "@/v2/components/ui/heading/Page";
import parseUrlToQueryParams from "../../utils/parseUrlToQueryParams";
import { DataTable, TableToolbar } from "@/v2/components/data-table/table";
import AccessControl from "@/v2/components/rbac/AccessControl";
import FindAllMobilization from "@/v2/api/mobilization/FindAll";
import { Mobilization } from "@/v2/types";
import { useAppSelector } from "@/v2/hooks/hooks";
import { hasRole } from "@/v2/utils/hasRole";
import { ColumnDef } from "@tanstack/react-table";
import { FilterColumn } from "@/v2/components/filter";
import ExportMobilization from "@/v2/api/mobilization/Export";

const RESTRICTED_COLUMNS = new Set([
    "client_commission",
    "ar_four_percent",
    "food_accommodation_and_travel",
]);

const getMobilizationColumns = <
    T extends { accessorKey?: string; id?: string },
>(
    Columns: T[],
    isAdmin: boolean,
    filter = false,
): T[] => {
    if (isAdmin) {
        return Columns;
    }

    const restrictedSet = new Set(RESTRICTED_COLUMNS);

    if (filter) {
        restrictedSet.add("employee.full_name");
        restrictedSet.add("ar_coordinator.full_name");
        restrictedSet.add("supporting_coordinator.full_name");
    }

    return Columns.filter(
        (col) => !restrictedSet.has(col.accessorKey! || col.id!),
    );
};

export default function MobilizationPage() {
    const [searchParams, _setSearchParams] = useSearchParams();
    const queryParams = parseUrlToQueryParams(searchParams);
    const user = useAppSelector((state) => state.user.value);

    const { skip, limit, search, filter } = queryParams;

    const isAdmin = hasRole(user, [
        "MANAGING_DIRECTOR",
        "HR_ADMINISTRATION_OFFICER",
    ]);

    const FilteredMobilizationColumn = getMobilizationColumns<
        ColumnDef<Mobilization, any>
    >(MobilizationColumns, isAdmin);

    const FilteredMobilizationFilterColumn = getMobilizationColumns<
        FilterColumn<Mobilization>
    >(MobilizationFilterColumn, isAdmin, true);

    const { refetch, ...query } = useQuery({
        queryKey: ["mobilizations", skip, limit, search, filter],
        queryFn: () =>
            FindAllMobilization({
                limit: limit ?? 20,
                skip: skip ?? 0,
                search: search,
                filter: filter,
            }),
    });

    return (
        <>
            <div className="flex flex-wrap items-center justify-between gap-4 py-2">
                <PageHeading
                    title="Mobilization Database"
                    description="Manage and monitor all mobilizations in your platform"
                />

                <div className="flex gap-4">
                    <AccessControl
                        module="MOBILIZATION_MANAGEMENT"
                        requestedAccess={"POST"}
                    >
                        <NavLink to={"create"}>
                            <Button>Add New Mobilization</Button>
                        </NavLink>
                    </AccessControl>
                </div>
            </div>
            <Card className="p-2">
                <TableToolbar<Mobilization>
                    column={FilteredMobilizationFilterColumn}
                    searchPlaceHolder="Search Mobilizations.."
                    exportFunc={() =>
                        ExportMobilization({
                            filter,
                            limit: query?.data?.pagination?.total_data_count,
                        })
                    }
                    exportFileName="mobilization"
                />

                <DataTable<Mobilization>
                    data={query.data?.data || []}
                    columns={FilteredMobilizationColumn}
                    loading={query.isLoading || query.isFetching}
                    pagination={query?.data?.pagination!}
                    specialRow={(row, i) => (
                        <MobilizationTableRow key={row.id} row={row} i={i} />
                    )}
                />
            </Card>
        </>
    );
}
